import React, { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { login, clearError } from '../../store/slices/authSlice';
import { LoginDto } from '../../../domain/entities';
import { ROUTES } from '../../../core/config/constants';

const schema = yup.object({
  mail: yup.string().email('Email invalide').required('Email requis'),
  password: yup.string().required('Mot de passe requis'),
});

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginDto>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.BOTTLES);
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = async (data: LoginDto) => {
    console.log('🚀 DÉBUT - Fonction onSubmit appelée');
    console.log('🔐 Tentative de connexion avec:', data);
    console.log('📡 URL API:', process.env.REACT_APP_API_URL);
    
    try {
      const result = await dispatch(login(data));
      console.log('✅ Réponse de connexion:', result);
    } catch (error) {
      console.error('❌ Erreur lors de la connexion:', error);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    console.log('📝 Formulaire soumis - événement:', e);
    handleSubmit(onSubmit)(e);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      {/* Logo Vinosk */}
      <Box
        component="img"
        src="https://vinosk.fr/images/logo-white.png"
        alt="Vinosk Logo"
        sx={{
          width: 120,
          height: 120,
          mb: 4,
        }}
      />

      {/* Titre "Connexion" */}
      <Typography
        variant="h4"
        sx={{
          color: 'white',
          fontWeight: 300,
          mb: 6,
          fontSize: '2rem',
        }}
      >
        Connexion
      </Typography>

      {/* Formulaire */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 350,
          backgroundColor: 'rgba(80, 80, 80, 0.3)',
          borderRadius: 3,
          padding: 4,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: '#8B2F54', color: 'white' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleFormSubmit} sx={{ width: '100%' }}>
          <TextField
            {...register('mail')}
            fullWidth
            id="mail"
            placeholder="Email"
            name="mail"
            autoComplete="email"
            autoFocus
            error={!!errors.mail}
            helperText={errors.mail?.message}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  borderWidth: 1,
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#ff6b6b',
              },
            }}
          />

          <TextField
            {...register('password')}
            fullWidth
            name="password"
            placeholder="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 4,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  borderWidth: 1,
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#ff6b6b',
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: '#8B2F54',
              color: 'white',
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 500,
              borderRadius: 2,
              mb: 4,
              '&:hover': {
                backgroundColor: '#A03965',
              },
              '&:disabled': {
                backgroundColor: '#666',
                color: '#ccc',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Se connecter'
            )}
          </Button>
        </Box>
      </Box>

      {/* Inscription et slogan */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
          Pas encore de compte ?{' '}
          <Link
            component={RouterLink}
            to={ROUTES.REGISTER}
            sx={{
              color: '#8B2F54',
              textDecoration: 'underline',
              fontWeight: 500,
              '&:hover': {
                color: '#A03965',
              },
            }}
          >
            Créer un compte
          </Link>
        </Typography>

        {/* Slogan en bas */}
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.9rem',
            fontStyle: 'italic',
          }}
        >
          Gérez votre cave en toute simplicité
        </Typography>
      </Box>
    </Box>
  );
};